/**
 * `/cards` route. Lists saved cards and offers "Pay with this card" CTA
 * that deep-links into `/checkout?storedPaymentMethodId=...&type=onetime`.
 */

import React, { useEffect } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import PageShell from '../../components/common/PageShell';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { parseSavedCardsParams } from '../../lib/urlParams';
import { useQuery } from '../../utils/hooks';
import { UI_CONFIG } from '../../utils/constants';

import SavedCardList from './components/SavedCardList';
import { loadSavedCards, resetSavedCards } from './savedCardsSlice';

const SavedCardsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const params = parseSavedCardsParams(query);
  const { items, loading, error } = useAppSelector((state) => state.savedCards);

  useEffect(() => {
    if (!params) return;
    void dispatch(loadSavedCards({ tokenId: params.tokenId, view: 'smarthq' }));
    return () => {
      dispatch(resetSavedCards());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.tokenId]);

  if (!params) {
    return (
      <PageShell>
        <div role="alert">Invalid cards link — missing required parameters.</div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div role="alert">
          {error.code}: {error.message}
        </div>
      </PageShell>
    );
  }

  const onPayWithThis = (storedPaymentMethodId: string): void => {
    const search = new URLSearchParams({
      tokenId: params.tokenId,
      country: params.country,
      storedPaymentMethodId,
    });
    navigate(`/checkout?${search.toString()}`);
  };

  const onAddCard = (): void => {
    const search = new URLSearchParams({
      tokenId: params.tokenId,
      country: params.country,
    });
    navigate(`/save-card?${search.toString()}`);
  };

  const showSavedBanner = query.get('saved') === '1';

  return (
    <PageShell loading={loading}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={{ color: UI_CONFIG.TEXT_COLOR, fontWeight: 600, mb: 0.5 }}>
            Saved card
          </Typography>
          <Typography sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: '0.9rem' }}>
            SmartHQ uses your newest saved card for quick checkout, or you can add a new one.
          </Typography>
        </Box>

        {showSavedBanner ? <Alert severity="success">Your card has been saved.</Alert> : null}

        <SavedCardList items={items} onPayWithThis={onPayWithThis} onAddCard={onAddCard} />

        {items.length > 0 ? (
          <Button variant="outlined" onClick={onAddCard}>
            Add new card
          </Button>
        ) : null}
      </Box>
    </PageShell>
  );
};

export default SavedCardsPage;
